import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { Client } from '@app/core/types/client';
import { DocViewerSlot } from '../identity-document-viewer/identity-document-viewer.component';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { IdentityDocument } from '@app/core/types/client-document';
import { BrandCodes } from '@app/core/types/brand';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DOCUMENT_STATUS,
  SLOT_CONFIGS,
  SLOT_KEYS,
  SlotKey,
  VerificationDetail,
  parseDocumentStatus,
} from '@app/core/types/id-verification';

interface DocSlotState {
  list: IdentityDocument[];
  selectedId: number | null;
  uploading: boolean;
  subject$: BehaviorSubject<DocViewerSlot>;
}

const IN_PROGRESS_STATUSES: DOCUMENT_STATUS[] = [
  DOCUMENT_STATUS.PROVIDED,
  DOCUMENT_STATUS.VALIDATING_SIMPLE,
  DOCUMENT_STATUS.VALIDATING_COMPLEX,
];

@Component({
  selector: 'itfg-identity-verification-documents',
  templateUrl: './identity-verification-documents.component.html',
  styleUrl: './identity-verification-documents.component.scss',
  standalone: false,
})
export class IdentityVerificationDocumentsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() detail$!: BehaviorSubject<VerificationDetail | null>;
  @Input() client!: Client;

  detail: VerificationDetail | null = null;
  loadingLists = false;

  // Expose to template
  readonly SLOT_CONFIGS = SLOT_CONFIGS;
  readonly SLOT_KEYS = SLOT_KEYS;

  readonly slots: Record<SlotKey, DocSlotState> = {
    front: { list: [], selectedId: null, uploading: false, subject$: new BehaviorSubject<DocViewerSlot>({ id: null }) },
    back: { list: [], selectedId: null, uploading: false, subject$: new BehaviorSubject<DocViewerSlot>({ id: null }) },
    selfie: { list: [], selectedId: null, uploading: false, subject$: new BehaviorSubject<DocViewerSlot>({ id: null }) },
  };

  // ── Display config ─────────────────────────────────────────────────

  readonly STATUS_ICON: Record<DOCUMENT_STATUS, string> = {
    [DOCUMENT_STATUS.PENDING]: 'hourglass_empty',
    [DOCUMENT_STATUS.PROVIDED]: 'hourglass_empty',
    [DOCUMENT_STATUS.VALIDATING_SIMPLE]: 'hourglass_empty',
    [DOCUMENT_STATUS.VALIDATING_COMPLEX]: 'hourglass_empty',
    [DOCUMENT_STATUS.INVALID]: 'cancel',
    [DOCUMENT_STATUS.VALID]: 'check_circle',
  };

  readonly STATUS_CLASS: Record<DOCUMENT_STATUS, string> = {
    [DOCUMENT_STATUS.PENDING]: 'pending',
    [DOCUMENT_STATUS.PROVIDED]: 'provided',
    [DOCUMENT_STATUS.VALIDATING_SIMPLE]: 'validating_simple',
    [DOCUMENT_STATUS.VALIDATING_COMPLEX]: 'validating_complex',
    [DOCUMENT_STATUS.INVALID]: 'invalid',
    [DOCUMENT_STATUS.VALID]: 'valid',
  };

  // ── Consolidated status ────────────────────────────────────────────

  get consolidatedStatus(): DOCUMENT_STATUS {
    const bases = SLOT_KEYS.map(key => {
      const raw = this.detail?.[SLOT_CONFIGS[key].statusKey] as string ?? '';
      return parseDocumentStatus(raw).base;
    });

    if (bases.includes(DOCUMENT_STATUS.INVALID)) {
      return DOCUMENT_STATUS.INVALID;
    }

    if (bases.some(base => IN_PROGRESS_STATUSES.includes(base))) {
      return DOCUMENT_STATUS.VALIDATING_SIMPLE;
    }

    return DOCUMENT_STATUS.VALID;
  }

  get consolidatedStatusClass(): string {
    return this.STATUS_CLASS[this.consolidatedStatus];
  }

  get consolidatedStatusIcon(): string {
    return this.STATUS_ICON[this.consolidatedStatus];
  }

  private _unsubscribe = new Subject<void>();

  constructor(
    private service: IdentityReportsService,
    private snackBar: MatSnackBar,
  ) { }

  // ── Lifecycle ──────────────────────────────────────────────────────

  ngOnInit(): void {
    this.detail$
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(detail => {
        this.detail = detail;
        for (const key of SLOT_KEYS) {
          const docId = (detail?.[SLOT_CONFIGS[key].docKey] as number | null) ?? null;
          this._selectDoc(key, docId);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['client'] && this.client?.civilId) {
      this._loadDocumentLists();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  // ── Data ──────────────────────────────────────────────────────────

  private _loadDocumentLists(): void {
    this.loadingLists = true;

    this.service
      .getIdentityDocumentList$(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (docs: IdentityDocument[]) => {
          for (const key of SLOT_KEYS) {
            this.slots[key].list = docs.filter(d => d.type.name === SLOT_CONFIGS[key].apiType);
          }
        },
        error: e => console.error(e),
        complete: () => {
          this.loadingLists = false;
        },
      });
  }

  // ── Selection ─────────────────────────────────────────────────────

  selectDocument(key: SlotKey, doc: IdentityDocument): void {
    this._selectDoc(key, doc.id);
  }

  private _selectDoc(key: SlotKey, id: number | null): void {
    this.slots[key].selectedId = id;
    this.slots[key].subject$.next({ id });
  }

  // ── Submit ────────────────────────────────────────────────────────

  submitDocument(key: SlotKey): void {
    const id = this.slots[key].selectedId;
    if (id == null) {
      return;
    }
    // TODO: wire to service
    this.snackBar.open(`Document #${id} submitted for verification`, 'OK', { duration: 3000 });
  }

  // ── Upload ────────────────────────────────────────────────────────

  onFileSelected(key: SlotKey, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    input.value = '';

    const formData = new FormData();
    formData.append('file', file);

    this.slots[key].uploading = true;

    this.service
      .addIdentityDocument$(
        this.client.civilId,
        this.client.brand.code as BrandCodes,
        SLOT_CONFIGS[key].apiType,
        formData,
      )
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (newDoc: IdentityDocument) => {
          this.slots[key].list.unshift(newDoc);
          this._selectDoc(key, newDoc.id);
          this.snackBar.open('Document uploaded', 'OK', { duration: 2000 });
        },
        error: e => {
          console.error(e);
          this.snackBar.open('Upload failed', 'Dismiss', { duration: 3000 });
        },
        complete: () => {
          this.slots[key].uploading = false;
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────

  /** Returns true when the given doc id is the one linked to this verification slot. */
  isVerificationDoc(key: SlotKey, id: number | null): boolean {
    if (id == null) {
      return false;
    }
    return this.detail?.[SLOT_CONFIGS[key].docKey] === id;
  }

  /**
   * Returns a simplified CSS modifier ('valid' | 'validating' | 'invalid')
   * for the document currently linked to the given verification slot.
   * Used to colour the row without adding per-status classes to the template.
   */
  verificationDocStatusClass(key: SlotKey): 'valid' | 'validating' | 'invalid' {
    const raw = this.detail?.[SLOT_CONFIGS[key].statusKey] as string ?? '';
    const base = parseDocumentStatus(raw).base;

    if (base === DOCUMENT_STATUS.VALID) {
      return 'valid';
    }

    if (IN_PROGRESS_STATUSES.includes(base)) {
      return 'validating';
    }

    return 'invalid';
  }

  trackById(_: number, doc: IdentityDocument): number {
    return doc.id;
  }
}